import React, { useEffect } from 'react';
import '../styles/About.css';
import ModernCTA from '../components/ModernCTA.js';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import WebMaint from '../assets/Web Maintenance.jpg';

const About = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.05, // Lower threshold for better mobile detection
    triggerOnce: false,
    rootMargin: '-50px 0px'
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const stats = [
    { value: '15+', label: 'Years Experience', icon: '🏆' },
    { value: '100+', label: 'Team Members', icon: '👥' },
    { value: '500+', label: 'Clients Worldwide', icon: '🌎' },
    { value: '200+', label: 'Projects Completed', icon: '🚀' },
    { value: '95%', label: 'Client Retention', icon: '❤️' }
  ];

  const services = [
    {
      title: 'Software Development',
      description: 'Custom solutions tailored to your business needs with agile methodologies.',
      icon: '💻'
    },
    {
      title: 'Web Development',
      description: 'Modern, responsive websites and web applications with cutting-edge tech.',
      icon: '🌐'
    },
    {
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile applications for iOS and Android.',
      icon: '📱'
    },
    {
      title: 'IT Consulting',
      description: 'Strategic guidance to optimize your IT infrastructure and operations.',
      icon: '📊'
    },
    {
      title: 'Cloud Solutions',
      description: 'Scalable cloud architecture and migration services for all platforms.',
      icon: '☁️'
    },
    {
      title: 'Corporate Training',
      description: 'Upskill your workforce with our specialized IT training programs.',
      icon: '🎓'
    }
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="about-page" ref={ref}>
      {/* Hero Section */}
      <motion.section 
        className="hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.h1
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Innovating IT Solutions Since 2007
          </motion.h1>
          <motion.p 
            className="subtitle"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Empowering businesses worldwide with cutting-edge technology
          </motion.p>
          <motion.div
            className="scroll-indicator"
            animate={{
              y: [0, 10, 0],
              opacity: [0.6, 1, 0.6]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            ↓
          </motion.div>
        </div>
      </motion.section>

      {/* About Content */}
      <motion.section 
        className="about-content"
        initial="hidden"
        animate={controls}
        variants={staggerContainer}
      >
        <div className="container">
          <motion.div className="about-grid" variants={staggerContainer}>
            <motion.div className="about-text" variants={fadeIn}>
              <h2>Our Story</h2>
              <p>
                Established in 2007, PCDS Infotech has grown from a small startup to a global IT powerhouse with over 15,000 professionals. 
                Our journey began with a simple mission: to deliver exceptional IT solutions that drive real business value.
              </p>
              <p>
                Over the years, we've expanded our expertise across industries, helping organizations navigate digital transformation. 
                From Fortune 500 companies to innovative startups, we've been the technology partner of choice for businesses seeking 
                reliable, scalable, and innovative solutions.
              </p>
              <div className="highlight-box">
                <h3>Why Choose Us?</h3>
                <ul>
                  <li>Proven track record with 500+ satisfied clients</li>
                  <li>15+ years of industry experience</li>
                  <li>Global delivery model with local expertise</li>
                  <li>Innovation-driven approach</li>
                </ul>
              </div>
            </motion.div>
            <motion.div className="about-image" variants={fadeIn}>
              <img 
                src={WebMaint} 
                alt="Our team working together" 
              />
              <div className="image-overlay"></div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Mission Section */}
      <motion.section 
        className="mission-section"
        initial="hidden"
        animate={controls}
        variants={staggerContainer}
      >
        <div className="container">
          <motion.div variants={fadeIn}>
            <h2>Our Vision & Mission</h2>
            <div className="mission-grid">
              <div className="mission-card">
                <div className="mission-icon">👁️</div>
                <h3>Vision</h3>
                <p>
                  To be the most trusted global IT solutions provider, recognized for innovation, 
                  quality, and transformative impact on businesses worldwide.
                </p>
              </div>
              <div className="mission-card">
                <div className="mission-icon">🎯</div>
                <h3>Mission</h3>
                <p>
                  Empower businesses through cutting-edge technology solutions that drive efficiency, 
                  growth, and competitive advantage in the digital era.
                </p>
              </div>
              <div className="mission-card">
                <div className="mission-icon">💎</div>
                <h3>Values</h3>
                <p>
                  Integrity, Innovation, Excellence, Collaboration, and Client-Centric approach 
                  form the foundation of everything we do.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Services Section */}
      <motion.section 
        className="services-section"
        initial="hidden"
        animate={controls}
        variants={staggerContainer}
      >
        <div className="container">
          <motion.div variants={fadeIn}>
            <h2>Our Core Services</h2>
            <p className="section-description">
              Comprehensive IT solutions tailored to your unique business requirements
            </p>
            <div className="services-grid">
              {services.map((service, index) => (
                <motion.div 
                  key={index}
                  className="service-card"
                  variants={fadeIn}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="service-icon">{service.icon}</div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Stats Section */}
      <motion.section 
        className="stats-section"
        initial="hidden"
        animate={controls}
        variants={staggerContainer}
      >
        <div className="container">
          <motion.div variants={fadeIn}>
            <h2>By The Numbers</h2>
            <p className="section-description">
              Our achievements in measurable success
            </p>
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <motion.div 
                  key={index}
                  className="stat-item"
                  variants={fadeIn}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="stat-icon">{stat.icon}</div>
                  <h3>{stat.value}</h3>
                  <p>{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <ModernCTA />
      </section>
    </div>
  );
};

export default About;